import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, Tag, ChevronRight } from 'lucide-react';

export default function ServiceCategory() {
  const { category } = useParams();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Map URL category to CMS category name
  const categoryMap = {
    'ansigtsbehandlinger': 'Ansigtsbehandling',
    'massage': 'Massage'
  };

  const displayTitle = category === 'ansigtsbehandlinger' ? 'Ansigtsbehandlinger' : 'Massage & Velvære';
  const cmsCategory = categoryMap[category] || 'Massage';

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/services');
        const data = await response.json();
        
        // Filter services by category
        const filtered = data.filter(s => s.category?.da === cmsCategory);
        setServices(filtered);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
    window.scrollTo(0, 0);
  }, [category, cmsCategory]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#b0a697]"></div>
      </div>
    );
  }

  return (
    <div className="pt-64 pb-20 bg-stone-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav className="flex mb-8 text-sm font-medium text-stone-500 uppercase tracking-wider">
          <Link to="/" className="hover:text-[#b0a697] transition-colors">Forside</Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-stone-900">{displayTitle}</span>
        </nav>

        <div className="mb-16">
          <h1 className="text-4xl md:text-5xl font-serif text-stone-900 mb-6">{displayTitle}</h1>
          <p className="text-lg text-stone-600 max-w-3xl leading-relaxed">
            {category === 'ansigtsbehandlinger' 
              ? 'Forkæl dig selv med vores eksklusive ansigtsbehandlinger. Vi kombinerer traditionelle teknikker med moderne pleje for at give din hud fornyet glød, balance og vitalitet.'
              : 'Oplev den ultimative afslapning med vores professionelle massagebehandlinger. Fra traditionel Thai massage til beroligende oliemassage – vi hjælper dig med at finde ro og løsne op for spændinger.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div 
              key={service.id} 
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 border border-stone-100 flex flex-col"
            >
              <div className="p-8 flex-grow flex flex-col">
                <h3 className="text-xl font-serif text-stone-900 mb-3">{service.title?.da}</h3>
                <div 
                  className="text-stone-600 text-sm leading-relaxed mb-6 flex-grow"
                  dangerouslySetInnerHTML={{ __html: service.description?.da }}
                />
                
                <div className="flex flex-col gap-4 pt-6 border-t border-stone-50">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center text-stone-500">
                      <Clock className="w-4 h-4 mr-2" />
                      <span>{service.duration?.da}</span>
                    </div>
                    <div className="font-semibold text-stone-900">
                      {service.price} kr.
                    </div>
                  </div>
                  <Link 
                    to="/kontakt" 
                    className="text-sm font-bold text-[#b0a697] hover:text-[#9a8f7d] transition-colors uppercase tracking-widest flex items-center justify-center py-2 border border-[#b0a697]/20 rounded-lg hover:bg-[#b0a697]/5"
                  >
                    Bestil tid
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {services.length === 0 && (
          <div className="text-center py-20 bg-white rounded-3xl border border-stone-100 shadow-sm">
            <Tag className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-serif text-stone-900 mb-2">Ingen behandlinger fundet</h3>
            <p className="text-stone-500">Vi opdaterer snart vores udvalg af {displayTitle.toLowerCase()}.</p>
          </div>
        )}
      </div>
    </div>
  );
}
